<template>
  <div>
    <FreeBottomSheet
      :name="name"
      @onClose="bottomSheet.hide(medicalInsuranceConfig.FORM_BOTTOM_SHEET)"
    >
      <template #header="{ hide }">
        <FreeHeader class="p-4 pb-2 text-center" is-centered>
          <template #default>
            <h2 class="text-text-neutral-primary text-base font-medium">
              {{ $t('ancillaries.medicalInsurance.title') }}
            </h2>
          </template>
          <template #rightAction>
            <FreeCloseButton @click="hide" />
          </template>
        </FreeHeader>
      </template>

      <template #default>
        <div class="space-y-4 px-4 pb-4 pt-2">
          <div v-if="isOneWay" class="relative">
            <FreeInput
              readonly
              :model-value="formattedEndDate"
              :has-error="v$.endDate.$error"
              @click="
                bottomSheet.show(medicalInsuranceConfig.END_DATE_BOTTOM_SHEET)
              "
            >
              <template #label>
                <p class="text-text-neutral-tertiary text-xs font-medium">
                  {{ $t('ancillaries.medicalInsurance.endDate') }}
                </p>
              </template>
              <template #errorMessage>
                <span
                  v-if="v$.endDate.$error"
                  class="text-text-negative-primary text-xs"
                >
                  {{ endDateErrorMessage }}
                </span>
              </template>
              <template #append>
                <!--eslint-disable-next-line-->
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="-mx-2"
                  width="24"
                  height="24"
                  fill="none"
                >
                  <path
                    fill="#4EBC73"
                    fill-rule="evenodd"
                    d="M8 3a1 1 0 0 1 1 1v1h6V4a1 1 0 1 1 2 0v1.027c.087.005.17.01.252.017.562.046 1.079.145 1.564.392a4 4 0 0 1 1.748 1.748c.247.485.346 1.002.392 1.564.044.541.044 1.206.044 2.01v4.483c0 .805 0 1.47-.044 2.01-.046.563-.145 1.08-.392 1.565a4 4 0 0 1-1.748 1.748c-.485.247-1.002.346-1.564.392-.541.044-1.206.044-2.01.044H8.758c-.805 0-1.47 0-2.01-.044-.563-.046-1.08-.145-1.565-.392a4 4 0 0 1-1.748-1.748c-.247-.485-.346-1.002-.392-1.564C3 16.71 3 16.046 3 15.242v-4.483c0-.805 0-1.47.044-2.01.046-.563.145-1.08.392-1.565a4 4 0 0 1 1.748-1.748c.485-.247 1.002-.346 1.564-.392L7 5.027V4a1 1 0 0 1 1-1ZM6.911 7.038c-.438.035-.663.1-.819.18a2 2 0 0 0-.874.874c-.08.156-.145.38-.18.819L5.03 9h13.938a5.475 5.475 0 0 0-.007-.089c-.035-.438-.1-.663-.18-.819a2 2 0 0 0-.874-.874c-.156-.08-.38-.145-.819-.18C16.639 7 16.057 7 15.2 7H8.8c-.857 0-1.439 0-1.889.038ZM5 15.2V11h14v4.2c0 .857 0 1.439-.038 1.889-.035.438-.1.663-.18.819a2 2 0 0 1-.874.874c-.156.08-.38.145-.819.18-.45.037-1.032.038-1.889.038H8.8c-.857 0-1.439 0-1.889-.038-.438-.035-.663-.1-.819-.18a2 2 0 0 1-.874-.874c-.08-.156-.145-.38-.18-.819C5 16.639 5 16.057 5 15.2Z"
                    clip-rule="evenodd"
                  />
                </svg>
              </template>
            </FreeInput>
          </div>
          <MIErrorCard
            v-if="isActualizingError"
            :is-loading="isActualizing"
            @onRetry="retryMutation"
            @onContinueWithoutInsurance="handleWithoutInsuranceContinue"
          />

          <div v-else class="space-y-4">
            <PriceLabels
              :amounts="priceLabelsAmounts"
              :initial-amount-id="initialAmountId"
              :product-ids="productIds"
              :offer-id="offerId"
              :is-loading="isActualizing"
              @onAmountSelected="handleAmountSelected"
              @onResetPassengers="emit('onUpdateProducts', [])"
            />
            <PassengerInput
              v-for="(passenger, index) in passengers"
              :key="passenger.productId"
              :passenger="passenger"
              :error-message="
                v$.passengers[index].passportDate.$error
                  ? v$.passengers[index].passportDate.$errors[0].$message
                  : ''
              "
              :is-loading="isActualizing"
              @onUpdateIsSelected="val => updateSelected(index, val)"
              @onUpdatePassportDate="val => updatePassportDate(index, val)"
            />
          </div>

          <FreeButton
            type="button"
            class="green-main-button sticky bottom-4 w-full"
            :disabled="isActualizing || isActualizingError"
            @click="
              buttonText === t('general.save')
                ? saveUnselectedPassengers()
                : submit()
            "
          >
            {{ buttonText }}
          </FreeButton>

          <i18n-t
            tag="p"
            class="text-text-neutral-primary text-center text-sm"
            keypath="ancillaries.medicalInsurance.offer.content"
          >
            <template #personalData>
              <button
                type="button"
                class="text-text-positive-primary"
                @click="
                  openInsuranceDocument(
                    appConfig.isDevRuntimeEnv,
                    medicalInsuranceConfig.PERSONAL_DATA_LINK,
                  )
                "
              >
                {{ t('ancillaries.medicalInsurance.offer.personalData') }}
              </button>
            </template>
            <template #publicOffer>
              <button
                type="button"
                class="text-text-positive-primary"
                @click="
                  bottomSheet.show(
                    medicalInsuranceConfig.PUBLIC_OFFER_BOTTOM_SHEET,
                  )
                "
              >
                {{ t('ancillaries.medicalInsurance.offer.publicOffer') }}
              </button>
            </template>
            <template #agreement>
              <button
                type="button"
                class="text-text-positive-primary"
                @click="
                  openInsuranceDocument(
                    appConfig.isDevRuntimeEnv,
                    medicalInsuranceConfig.RULES_FILE_LINK,
                  )
                "
              >
                {{ t('ancillaries.medicalInsurance.offer.agreement') }}
              </button>
            </template>
          </i18n-t>
        </div>
      </template>
    </FreeBottomSheet>
    <MedicalInsuranceSumBottomSheet
      :name="medicalInsuranceConfig.SUM_BOTTOM_SHEET"
    />
    <FreeSearchFormCalendar
      :name="medicalInsuranceConfig.END_DATE_BOTTOM_SHEET"
      :title="$t('ancillaries.medicalInsurance.endDate')"
      :selected-dates="[endDate]"
      :max-date="maxDate"
      :mode="'single'"
      is-new-calendar-version
      :marked-dates="[today]"
      @onDatesCommit="onEndDateSelect"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import useVuelidate from '@vuelidate/core';
  import { required } from '@vuelidate/validators';
  import { prettyPrice } from '@aviata/text-fns';
  import {
    FreeBottomSheet,
    FreeHeader,
    FreeCloseButton,
    FreeButton,
    FreeSearchFormCalendar,
    FreeInput,
  } from 'freedom-shared';
  import dayjs from 'dayjs';
  import type { AxiosResponse } from 'axios';
  import {
    controllerModel,
    dateUtils,
    openInsuranceDocument,
    validationI18n,
  } from 'shared';

  import { MIErrorCard } from '@/widgets/medical-insurance';

  import type { bookingStateTypes } from '@/features/booking-state';

  import { appConfig } from '@/shared/config';

  import PriceLabels from './PriceLabels.vue';
  import PassengerInput from './PassengerInput.vue';
  import MedicalInsuranceSumBottomSheet from './MedicalInsuranceSumBottomSheet.vue';
  import type {
    MedicalInsuranceFormPassenger,
    UpdateMIProductsData,
  } from '../types';
  import {
    createPassportDateValidators,
    useMiProductsActualize,
    useMedicalInsuranceStore,
  } from '../model';
  import { medicalInsuranceConfig } from '../config';
  import { normalizePassengers } from '../lib';

  const props = defineProps<{
    name: string;
    offerId: string;
    isOneWay: boolean;
  }>();

  const emit = defineEmits<{
    onUpdateProducts: [bookingStateTypes.MedicalInsuranceProductsState[]];
  }>();

  const medicalInsuranceStore = useMedicalInsuranceStore();
  const passengersWithMedicalInsurance = computed(
    () => medicalInsuranceStore.passengersWithMedicalInsurance,
  );

  const today = dayjs().format('YYYY-MM-DD');
  const { t } = useI18n();

  const passengers = ref<MedicalInsuranceFormPassenger[]>([]);

  const { withI18nMessage } = validationI18n.model.useLocalizedValidators(
    'ancillaries.medicalInsurance.validations',
  );
  const { isDateInRange, requiredIfSelected } = createPassportDateValidators();

  const productIds = computed(() =>
    passengersWithMedicalInsurance?.value.map(p => p.productId),
  );

  const priceLabelsAmounts = computed(
    () => passengersWithMedicalInsurance?.value?.[0]?.policyPayout || [],
  );

  const initialAmountId = computed(
    () => passengersWithMedicalInsurance?.value?.[0]?.currentPriceId || '',
  );

  const bottomSheet = controllerModel.useBottomSheet();

  const mapPassengers = (passengers: MedicalInsuranceFormPassenger[]) => {
    return passengers
      ?.map(passenger => {
        if (passenger) {
          return {
            isSelected: false,
            passportDate: '',
            fullName: passenger.fullName,
            firstName: passenger.firstName,
            lastName: passenger.lastName,
            productPriceAmount: passenger.productPriceAmount,
            type: passenger.type,
            productId: passenger.productId,
            idx: passenger.idx,
          };
        }
        return null;
      })
      .filter(Boolean) as MedicalInsuranceFormPassenger[];
  };

  // TODO: добавить логику валидации и отоброжения ошибок на
  //  странице услуг связанных с мст + не давать закрыть
  //  окно если пассажиры не провалидированны, потому-что сейчас
  //  если все пользователи выбраны, то флоу блочиться
  //  потому-что поля не заполнены, а пассажиры выбраны
  //  https://tracker.yandex.ru/AVIATA-16003
  watch(
    () => medicalInsuranceStore.passengersWithMedicalInsurance,
    newVal => {
      passengers.value = mapPassengers(newVal);
    },
    { immediate: true, deep: true },
  );

  const buttonText = computed(() => {
    const selectedPassengers = passengers.value.filter(p => p.isSelected);
    const totalAmount = selectedPassengers.reduce(
      (sum, p) => sum + (Number(p.productPriceAmount) || 0),
      0,
    );

    if (selectedPassengers.length === 0) {
      return t('general.save');
    }

    return totalAmount === 0
      ? t('ancillaries.medicalInsurance.addFree') // Добавить бесплатно
      : t('ancillaries.medicalInsurance.addFor', {
          price: prettyPrice(totalAmount.toString()),
        }); // Добавить за XXX ₸
  });

  const endDate = ref('');

  const state = { passengers, endDate };

  const rules = computed(() => ({
    passengers: passengers.value
      ? passengers.value.map(() => ({
          passportDate: {
            requiredIfSelected: withI18nMessage(requiredIfSelected),
            isDateInRange: withI18nMessage(isDateInRange),
          },
        }))
      : {},
    endDate:
      props.isOneWay && passengers.value.some(p => p.isSelected)
        ? { required }
        : {},
  }));

  const v$ = useVuelidate(rules, state);
  const selectedAmountId = ref<string | null>(null);

  const {
    mutate: mutateMIProducts,
    isLoading: isActualizing,
    isError: isActualizingError,
  } = useMiProductsActualize(props.offerId);

  const endDateErrorMessage = computed(() => {
    if (props.isOneWay && v$.value.endDate.$error) {
      return t('ancillaries.medicalInsurance.validations.requiredEndDate');
    }
    return '';
  });

  watch(
    () => bottomSheet.isVisible(medicalInsuranceConfig.FORM_BOTTOM_SHEET),
    isVisible => {
      if (isVisible && props.isOneWay && endDate.value === '') {
        bottomSheet.show(medicalInsuranceConfig.END_DATE_BOTTOM_SHEET);
      }
    },
  );

  watch(
    () => bottomSheet.isVisible(medicalInsuranceConfig.END_DATE_BOTTOM_SHEET),
    isVisible => {
      if (!isVisible && props.isOneWay && endDate.value === '') {
        bottomSheet.hide(medicalInsuranceConfig.FORM_BOTTOM_SHEET);
      }
    },
  );

  function updatePassportDate(index: number, value: string) {
    passengers.value[index].passportDate = value.trim();
  }

  function updateSelected(index: number, value: boolean) {
    passengers.value[index].isSelected = value;
  }

  const maxDate = dayjs().add(1, 'year').toString();
  const formattedEndDate = computed(() => {
    return endDate.value ? dateUtils.format(endDate.value, 'DD MMM dd') : '';
  });

  function retryMutation() {
    const data: UpdateMIProductsData = {};

    if (endDate.value) {
      data.end_date = endDate.value;
    }

    if (selectedAmountId.value) {
      data.amount_id = selectedAmountId.value;
    }

    updateMIProducts(data);
  }

  function updateMIProducts(data: UpdateMIProductsData) {
    mutateMIProducts(
      productIds.value.map(id => ({
        id,
        data,
      })),
      {
        onSuccess: (
          updatedProducts: AxiosResponse<MedicalInsuranceFormPassenger[]>,
        ) => {
          medicalInsuranceStore.updateMedicalInsuranceProducts(
            updatedProducts.data,
          );
        },
        onSettled: () => {
          emit('onUpdateProducts', []);
        },
      },
    );
  }

  function onEndDateSelect(dates: string[]) {
    endDate.value = dates[0];
    updateMIProducts({ end_date: endDate.value });
  }

  function handleAmountSelected(amountId: string) {
    selectedAmountId.value = amountId;
    updateMIProducts({ amount_id: amountId });
  }

  function submit() {
    v$.value.$touch();
    if (v$.value.$invalid) {
      return;
    }

    const normalizedPassengers = normalizePassengers(passengers.value);

    emit('onUpdateProducts', normalizedPassengers);
    bottomSheet.hide(medicalInsuranceConfig.FORM_BOTTOM_SHEET);
  }

  function saveUnselectedPassengers() {
    emit('onUpdateProducts', []);
    bottomSheet.hide(medicalInsuranceConfig.FORM_BOTTOM_SHEET);
  }

  const handleWithoutInsuranceContinue = () => {
    passengers.value = passengers.value.map(pax => ({
      ...pax,
      passportDate: '',
      isSelected: false,
    }));
    emit('onUpdateProducts', []);
    bottomSheet.hide(medicalInsuranceConfig.FORM_BOTTOM_SHEET);
  };
</script>
